/**
 * GTM UX Event Tracker
 *
 * Non-click events: page views, scroll depth, viewport visibility, hover, form/auth.
 * Click events use gtm_ux_* CSS classes on DOM elements — detected by GTM Click triggers.
 *
 * All dataLayer events include global properties: platform, user_logged_in, user_state, source_page
 */
(function () {
  "use strict";

  // ============================================================
  // CORE UTILITIES
  // ============================================================

  function getSourcePage() {
    var path = window.location.pathname;
    if (path === "/" || path === "/hot" || path === "/new" || path === "/discussed") return "home_feed";
    if (/^\/deals\/new/.test(path)) return "deal_form";
    if (/^\/deals\//.test(path)) return "deal_detail";
    if (/^\/coupons\/new/.test(path)) return "coupon_form";
    if (path === "/coupons") return "coupons_page";
    if (/^\/coupons\//.test(path)) return "coupon_detail";
    var storePrefix = (window.appconfig && window.appconfig.store_url_prefix) || "stores";
    if (new RegExp("^\\/" + storePrefix + "\\/").test(path)) return "store_page";
    if (path === "/groups") return "all_groups";
    if (/^\/groups\//.test(path)) return "group_detail";
    if (path === "/discussions") return "discussions_page";
    if (/^\/discussions\/new/.test(path)) return "discussion_form";
    if (/^\/discussions\//.test(path)) return "discussion_detail";
    if (/^\/forums\//.test(path)) return "forum_detail";
    if (path === "/credit-cards") return "credit_cards";
    if (path === "/login" || path === "/signup") return "auth_page";
    if (path === "/search_result") return "search_results";
    if (/^\/profile\//.test(path)) return "user_profile";
    if (path === "/settings") return "settings";
    if (/^\/news\/new/.test(path)) return "news_form";
    if (/^\/news\//.test(path)) return "news_detail";
    if (path === "/news") return "news_page";
    if (/^\/stores/.test(path) || path === "/all-stores") return "all_stores";
    return "other";
  }

  function getGlobalProperties() {
    var body = document.body;
    var loggedIn = body && body.dataset.gtmLoggedIn === "true";
    return {
      platform: window.innerWidth < 768 ? "msite" : "desktop",
      user_logged_in: loggedIn,
      user_state: loggedIn ? "logged_in" : "anonymous",
      source_page: getSourcePage()
    };
  }

  function pushEvent(eventName, additionalData) {
    window.dataLayer = window.dataLayer || [];
    var payload = { event: "gtm_ux_" + eventName };
    var globals = getGlobalProperties();
    for (var key in globals) {
      if (globals.hasOwnProperty(key)) payload[key] = globals[key];
    }
    if (additionalData) {
      for (var k in additionalData) {
        if (additionalData.hasOwnProperty(k)) payload[k] = additionalData[k];
      }
    }
    window.dataLayer.push(payload);
  }

  // Expose globally for use by page-specific JS if needed
  window.gtmUxTrack = pushEvent;

  function throttle(fn, delay) {
    var lastCall = 0;
    return function () {
      var now = Date.now();
      if (now - lastCall >= delay) {
        lastCall = now;
        fn.apply(this, arguments);
      }
    };
  }

  function debounce(fn, delay) {
    var timer;
    return function () {
      var context = this;
      var args = arguments;
      clearTimeout(timer);
      timer = setTimeout(function () {
        fn.apply(context, args);
      }, delay);
    };
  }

  // ============================================================
  // PAGE VIEW EVENTS
  // ============================================================

  function initPageViewEvents() {
    var path = window.location.pathname;

    // Home Feed
    if (path === "/" || path === "/hot" || path === "/new" || path === "/discussed") {
      var defaultTab = "hot";
      if (path === "/new") defaultTab = "new";
      else if (path === "/discussed") defaultTab = "discussed";
      pushEvent("home_deals_feed_viewed", { default_tab: defaultTab });
    }

    // Deal Detail
    var topicEl = document.querySelector("[data-topic-id]");
    if (getSourcePage() === "deal_detail" && topicEl) {
      pushEvent("deal_detail_viewed", {
        deal_id: topicEl.dataset.topicId || "",
        source: document.referrer ? "referrer" : "direct"
      });
    }

    // Store Page
    var storeSection = document.querySelector("[data-gtm-store-name]");
    if (getSourcePage() === "store_page") {
      pushEvent("store_page_viewed", {
        store_name: storeSection ? storeSection.dataset.gtmStoreName : "",
        source: document.referrer ? "referrer" : "direct"
      });
    }

    // Group Detail
    var groupSection = document.querySelector("[data-gtm-group-name]");
    if (getSourcePage() === "group_detail") {
      pushEvent("group_page_viewed", {
        group_name: groupSection ? groupSection.dataset.gtmGroupName : "",
        group_id: groupSection ? groupSection.dataset.gtmGroupId : "",
        source: document.referrer ? "referrer" : "direct"
      });
    }

    // All Coupons
    if (path === "/coupons") {
      pushEvent("all_coupons_page_viewed", {
        source: document.referrer ? "referrer" : "direct"
      });
    }

    // All Stores
    if (getSourcePage() === "all_stores") {
      pushEvent("all_stores_page_viewed");
    }

    // All Groups
    if (path === "/groups") {
      pushEvent("all_groups_page_viewed");
    }

    // Discussions
    if (path === "/discussions") {
      pushEvent("all_discussions_page_viewed");
    }

    // Credit Cards
    if (path === "/credit-cards") {
      pushEvent("credit_cards_page_viewed");
    }

    // Auth Pages
    if (path === "/login" || path === "/signup") {
      pushEvent("auth_modal_viewed", {
        entry_point: "page",
        modal_type: path === "/login" ? "login" : "signup"
      });
    }

    // Form Pages
    if (/\/new$/.test(path) || /\/edit$/.test(path)) {
      var formType = "other";
      if (/deals/.test(path)) formType = "deal";
      else if (/discussions/.test(path)) formType = "discussion";
      else if (/coupons/.test(path)) formType = "coupon";
      else if (/news/.test(path)) formType = "news";
      else if (/feedbacks/.test(path)) formType = "feedback";
      pushEvent("form_viewed", {
        form_type: formType,
        source_page: getSourcePage()
      });
    }
  }

  // ============================================================
  // SCROLL & VIEWPORT EVENTS
  // ============================================================

  function initScrollEvents() {
    // Scroll depth tracking
    var trackedDepths = {};
    var depthMarkers = [25, 50, 75, 100];
    var currentPage = getSourcePage();

    // Only track scroll depth on relevant pages
    var scrollPages = ["home_feed", "deal_detail", "store_page", "group_detail", "discussions_page", "coupons_page"];
    if (scrollPages.indexOf(currentPage) >= 0) {
      var scrollHandler = throttle(function () {
        var scrollTop = window.pageYOffset || document.documentElement.scrollTop;
        var docHeight = document.documentElement.scrollHeight - window.innerHeight;
        if (docHeight <= 0) return;
        var scrollPercent = Math.round((scrollTop / docHeight) * 100);

        for (var i = 0; i < depthMarkers.length; i++) {
          var depth = depthMarkers[i];
          if (scrollPercent >= depth && !trackedDepths[depth]) {
            trackedDepths[depth] = true;

            var eventName = "home_feed_scrolled";
            if (currentPage === "store_page") eventName = "store_page_scrolled";
            else if (currentPage === "deal_detail") eventName = "comments_scrolled";

            pushEvent(eventName, {
              scroll_depth: depth,
              max_position_seen: scrollPercent
            });
          }
        }
      }, 500);

      window.addEventListener("scroll", scrollHandler, { passive: true });
    }

    // IntersectionObserver for section visibility
    if ("IntersectionObserver" in window) {
      // Comments section visible (deal detail)
      var commentsSection = document.querySelector("#comments, #comments-section, .comment-section");
      if (commentsSection) {
        var commentsObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              var topicEl = document.querySelector("[data-topic-id]");
              pushEvent("comments_scrolled", {
                deal_id: topicEl ? topicEl.dataset.topicId : "",
                scroll_depth: "comments_visible"
              });
              commentsObserver.disconnect();
            }
          });
        }, { threshold: 0.1 });
        commentsObserver.observe(commentsSection);
      }

      // Similar stores section visible
      var similarStoresSection = document.querySelector(".similar-stores, .similar-stores-section");
      if (similarStoresSection) {
        var storeSection = document.querySelector("[data-gtm-store-name]");
        var similarStoresObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              pushEvent("similar_stores_section_viewed", {
                store_name: storeSection ? storeSection.dataset.gtmStoreName : ""
              });
              similarStoresObserver.disconnect();
            }
          });
        }, { threshold: 0.1 });
        similarStoresObserver.observe(similarStoresSection);
      }

      // Similar groups section visible
      var similarGroupsSection = document.querySelector(".similar-groups, .related-groups, .similar-groups-section");
      if (similarGroupsSection) {
        var storeEl = document.querySelector("[data-gtm-store-name]");
        var similarGroupsObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              pushEvent("similar_groups_section_viewed", {
                store_name: storeEl ? storeEl.dataset.gtmStoreName : ""
              });
              similarGroupsObserver.disconnect();
            }
          });
        }, { threshold: 0.1 });
        similarGroupsObserver.observe(similarGroupsSection);
      }

      // Deal Baba banner visible
      var dealBabaBanner = document.querySelector(".deal-baba-banner, .ai-banner, .deal-baba-section");
      if (dealBabaBanner) {
        var dealBabaObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              pushEvent("deal_baba_banner_viewed", {
                banner_name: "deal_baba",
                page_type: getSourcePage()
              });
              dealBabaObserver.disconnect();
            }
          });
        }, { threshold: 0.1 });
        dealBabaObserver.observe(dealBabaBanner);
      }

      // Best coupons section visible
      var bestCouponsSection = document.querySelector(".best-coupons, .best-coupons-section");
      if (bestCouponsSection) {
        var bestCouponsObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              pushEvent("best_coupons_section_viewed");
              bestCouponsObserver.disconnect();
            }
          });
        }, { threshold: 0.1 });
        bestCouponsObserver.observe(bestCouponsSection);
      }

      // Top coupon stores section visible
      var topCouponStoresSection = document.querySelector(".top-merchants-swiper, .top-coupon-stores");
      if (topCouponStoresSection) {
        var topCouponStoresObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) {
              pushEvent("top_coupon_stores_section_viewed", {
                section_name: "top_coupon_stores"
              });
              topCouponStoresObserver.disconnect();
            }
          });
        }, { threshold: 0.1 });
        topCouponStoresObserver.observe(topCouponStoresSection);
      }
    }
  }

  // ============================================================
  // HOVER EVENTS (Desktop only)
  // ============================================================

  function initHoverEvents() {
    // Only on desktop
    if (window.innerWidth < 768) return;

    // Navigation item hover
    var navItems = document.querySelectorAll(".header-hover, .nav-primary a, .main-nav-item");
    navItems.forEach(function (navItem) {
      navItem.addEventListener("mouseenter", function () {
        pushEvent("top_nav_item_hovered", {
          nav_item: navItem.dataset.gtmNavItem || navItem.textContent.trim()
        });
      });
    });
  }

  // ============================================================
  // FORM & FOCUS EVENTS
  // ============================================================

  function initFormEvents() {
    // Search bar focused
    var searchBox = document.querySelector("#search-box, #searchbox, .search-input");
    if (searchBox) {
      searchBox.addEventListener("focus", function () {
        pushEvent("search_focused");
      });
    }

    // Search submitted
    var searchForm = searchBox ? searchBox.closest("form") : null;
    if (searchForm) {
      searchForm.addEventListener("submit", function () {
        pushEvent("search_submitted", {
          search_query: searchBox.value || ""
        });
      });
    }
    // Also handle Enter key in search box
    if (searchBox) {
      searchBox.addEventListener("keydown", function (e) {
        if (e.key === "Enter" || e.keyCode === 13) {
          pushEvent("search_submitted", {
            search_query: searchBox.value || ""
          });
        }
      });
    }

    // Comment input focused (reply box click to expand, then focus)
    var commentInputs = document.querySelectorAll(".reply-box-open-close, #reply_box");
    commentInputs.forEach(function (input) {
      input.addEventListener("click", function () {
        var topicEl = document.querySelector("[data-topic-id]");
        pushEvent("comment_input_focused", {
          deal_id: topicEl ? topicEl.dataset.topicId : ""
        });
      });
    });

    // Comment search used
    var commentSearch = document.querySelector(".comment-search-input, #comment-search, [name='comment_search']");
    if (commentSearch) {
      var commentSearchHandler = debounce(function () {
        if (commentSearch.value.length > 0) {
          var topicEl = document.querySelector("[data-topic-id]");
          pushEvent("comment_search_used", {
            deal_id: topicEl ? topicEl.dataset.topicId : "",
            search_query: commentSearch.value
          });
        }
      }, 500);
      commentSearch.addEventListener("input", commentSearchHandler);
    }

    // Coupon search started/submitted
    var couponSearch = document.querySelector("#search-box-coupons, .coupon-search-input");
    if (couponSearch) {
      couponSearch.addEventListener("focus", function () {
        pushEvent("coupon_search_started");
      });

      var couponSearchForm = couponSearch.closest("form");
      if (couponSearchForm) {
        couponSearchForm.addEventListener("submit", function () {
          pushEvent("coupon_search_submitted", {
            search_query: couponSearch.value || "",
            search_type: "coupon"
          });
        });
      }

      couponSearch.addEventListener("keydown", function (e) {
        if (e.key === "Enter" || e.keyCode === 13) {
          pushEvent("coupon_search_submitted", {
            search_query: couponSearch.value || "",
            search_type: "coupon"
          });
        }
      });
    }

    // Group search performed
    var groupSearch = document.querySelector("#group_search_input, .group-search-input");
    if (groupSearch) {
      var groupSearchHandler = debounce(function () {
        if (groupSearch.value.length > 0) {
          pushEvent("group_search_performed", {
            search_term: groupSearch.value
          });
        }
      }, 500);
      groupSearch.addEventListener("input", groupSearchHandler);
    }

    // Form abandonment tracking
    var formContainers = document.querySelectorAll("#new_topic, .edit_topic, form[action*='deals'], form[action*='discussions'], form[action*='coupons']");
    formContainers.forEach(function (form) {
      var formInteracted = false;
      var formSubmitting = false;
      var lastField = "";

      form.addEventListener("input", function (e) {
        formInteracted = true;
        lastField = e.target.name || e.target.id || "";
      });

      form.addEventListener("submit", function () {
        formSubmitting = true;
      });

      window.addEventListener("beforeunload", function () {
        if (formInteracted && !formSubmitting) {
          pushEvent("form_abandoned", {
            form_type: getSourcePage().replace("_form", ""),
            last_interacted_field: lastField
          });
        }
      });
    });

    // Auth form success/failure via Rails UJS events
    var authForms = document.querySelectorAll("form[action*='session'], form[action*='login'], #login-form, .login-form");
    authForms.forEach(function (form) {
      form.addEventListener("ajax:success", function () {
        pushEvent("login_success", {
          login_method: "email",
          entry_point: getSourcePage()
        });
      });
      form.addEventListener("ajax:error", function () {
        pushEvent("login_failed", {
          login_method: "email",
          error_type: "ajax_error",
          entry_point: getSourcePage()
        });
      });
    });

    var signupForms = document.querySelectorAll("#sign_up_form_page, #sign_up_form_modal, form[action*='user']");
    signupForms.forEach(function (form) {
      form.addEventListener("ajax:success", function () {
        pushEvent("auth_signup_success", {
          auth_method: "email",
          newsletter_opted_in: form.querySelector("input[name*='newsletter']") ? form.querySelector("input[name*='newsletter']").checked : false
        });
      });
      form.addEventListener("ajax:error", function () {
        pushEvent("auth_signup_failed", {
          auth_method: "email",
          newsletter_opted_in: form.querySelector("input[name*='newsletter']") ? form.querySelector("input[name*='newsletter']").checked : false
        });
      });
    });

    // Content form success/failure
    var contentForms = document.querySelectorAll("#new_topic, .edit_topic");
    contentForms.forEach(function (form) {
      form.addEventListener("ajax:success", function () {
        pushEvent("form_submit_success", {
          form_type: getSourcePage().replace("_form", ""),
          content_id: ""
        });
      });
      form.addEventListener("ajax:error", function () {
        pushEvent("form_submit_failed", {
          form_type: getSourcePage().replace("_form", ""),
          error_type: "ajax_error"
        });
      });
    });
  }

  // ============================================================
  // INITIALIZE ALL TRACKING
  // ============================================================

  document.addEventListener("DOMContentLoaded", function () {
    initPageViewEvents();
    initScrollEvents();
    initHoverEvents();
    initFormEvents();
  });
})();
